//*Libraries imports
import { useState, useRef, useEffect } from "react";

//*Components Imports
import SignaturePad from "signature_pad";
import { CircularProgress } from "@material-ui/core";
import { Modal, Button, Tab, Tabs } from "react-bootstrap";
import SweetAlert from "react-bootstrap-sweetalert";

//*Constants Imports
import { STORAGE_URL } from "../../../variables";
import { useSelector } from "react-redux";

function SignatureFormModal({
  show,
  handleClose,
  handleSave,
  existingSignature,
  loading,
}) {
  const initialFiles = {
    s1: null,
    s2: null,
    s3: null,
    c1: null,
    c2: null,
    c3: null,
  };

  const [key, setKey] = useState("1");
  const [isProcessing, setIsProcessing] = useState(false);
  const [alert, setAlert] = useState(null);
  const [modalEntered, setModalEntered] = useState(false);
  const [modes, setModes] = useState({ 1: "draw", 2: "draw", 3: "draw" });
  const [files, setFiles] = useState(initialFiles);
  const [previews, setPreviews] = useState({});
  const [imagesLoading, setImagesLoading] = useState(false);
  const user = useSelector(store=>store.AppReducer.user);

  const pads = useRef({});
  const canvasRefs = useRef({});

  const showAlert = (type, title, message) => {
    setAlert(
      <SweetAlert
        type={type}
        title={title}
        onConfirm={() => setAlert(null)}
        confirmBtnText="OK"
        confirmBtnBsStyle="primary"
      >
        {message}
      </SweetAlert>,
    );
  };

  const resizeCanvas = (canvas, pad) => {
    if (!canvas) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext("2d").scale(ratio, ratio);
    if (pad) pad.clear();
  };

  useEffect(() => {
    if (existingSignature) {
      setImagesLoading(true);
      const imagesToLoad = [];
      for (let i = 1; i <= 3; i++) {
        if (existingSignature[`caminho_arquivo_assinatura_${i}`]) {
          imagesToLoad.push(
            STORAGE_URL + existingSignature[`caminho_arquivo_assinatura_${i}`],
          );
        }
        if (existingSignature[`caminho_arquivo_carimbo_${i}`]) {
          imagesToLoad.push(
            STORAGE_URL + existingSignature[`caminho_arquivo_carimbo_${i}`],
          );
        }
      }

      if (imagesToLoad.length === 0) {
        setImagesLoading(false);
        return;
      }

      let loadedCount = 0;
      const checkDone = () => {
        loadedCount++;
        if (loadedCount === imagesToLoad.length) {
          setImagesLoading(false);
        }
      };

      imagesToLoad.forEach((src) => {
        const img = new Image();
        img.onload = checkDone;
        img.onerror = checkDone;
        img.src = src;
      });
    } else {
      setImagesLoading(false);
    }
  }, [existingSignature]);

  useEffect(() => {
    if (!show || !modalEntered) return;

    const k = parseInt(key);
    let timeoutId;

    const initPad = () => {
      const canvas = canvasRefs.current[k];
      if (modes[k] === "draw") {
        if (canvas && canvas.offsetWidth > 0) {
          const ratio = Math.max(window.devicePixelRatio || 1, 1);
          const targetWidth = canvas.offsetWidth * ratio;

          if (!pads.current[k] || pads.current[k]._canvas !== canvas) {
            if (pads.current[k]) pads.current[k].off();

            resizeCanvas(canvas);
            pads.current[k] = new SignaturePad(canvas, {
              backgroundColor: "rgba(255, 255, 255, 0)", // Fundo transparente
              minWidth: 1,
              maxWidth: 2.5,
              penColor: "black",
              velocityFilterWeight: 0.7,
            });
          } else if (canvas.width !== targetWidth) {
            resizeCanvas(canvas, pads.current[k]);
          }
        } else {
          timeoutId = setTimeout(initPad, 100);
        }
      }
    };

    timeoutId = setTimeout(initPad, 50);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [show, key, modes, modalEntered]);

  useEffect(() => {
    if (!show) {
      setFiles(initialFiles);
      setModes({ 1: "draw", 2: "draw", 3: "draw" });
      setKey("1");
      setModalEntered(false);
      Object.values(previews).forEach((url) => URL.revokeObjectURL(url));
      setPreviews({});
      Object.values(pads.current).forEach((p) => p.off());
      pads.current = {};
      setAlert(null);
      setImagesLoading(false);
    }
  }, [show]);

  const handleFile = (e, idx, type) => {
    if (e.target.files?.[0]) {
      const f = e.target.files[0];

      const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
      if (!allowedTypes.includes(f.type)) {
        showAlert(
          "error",
          "Arquivo Inválido",
          "Apenas arquivos PNG, JPG ou JPEG são permitidos.",
        );
        e.target.value = null;
        return;
      }

      const k = type === "sig" ? `s${idx}` : `c${idx}`;
      if (previews[k]) URL.revokeObjectURL(previews[k]);

      setFiles((prev) => ({ ...prev, [k]: f }));
      setPreviews((prev) => ({ ...prev, [k]: URL.createObjectURL(f) }));
    }
  };

  const save = async () => {
    const isUpdate = !!existingSignature?.id;
    const errors = [];
    let hasChanges = false;

    // 1. Validação
    for (let i = 1; i <= 3; i++) {
       if(user.email=='gislaina.sleal@adm.educacao.pe.gov.br' && i!=1){
          continue;
        }
        else if(user.email=='izabel.cprocha@adm.educacao.pe.gov.br' && i!=2){
          continue;

        }
        else if(user.email=='ana.elizabete1962@adm.educacao.pe.gov.br' && i!=3){
          continue;

        }

      const isDrawMode = modes[i] === "draw";
      const pad = pads.current[i];
      const fileSig = files[`s${i}`];
      const fileStamp = files[`c${i}`];

      // Verificação de Assinatura
      let hasNewSig = false;
      if (isDrawMode) {
        // Se o pad não foi inicializado (aba não visitada) ou está vazio, não tem assinatura nova
        hasNewSig = pad && !pad.isEmpty();
      } else {
        hasNewSig = !!fileSig;
      }

      const hasExSig = !!existingSignature?.[`caminho_arquivo_assinatura_${i}`];
      const hasSig = hasNewSig || hasExSig;

      // Verificação de Carimbo
      const hasNewStamp = !!fileStamp;
      const hasExStamp = !!existingSignature?.[`caminho_arquivo_carimbo_${i}`];
      const hasStamp = hasNewStamp || hasExStamp;

      if (hasNewSig || hasNewStamp) {
        hasChanges = true;
      }

      if (!isUpdate) {
        if (!hasSig) {
          errors.push(`A assinatura ${i} é obrigatória para o cadastro \n.`);
        }
        if (!hasStamp) {
          errors.push(`O carimbo ${i} é obrigatório para o cadastro \n.`);
        }
      } else {
        if (hasSig && !hasStamp) {
          errors.push(
            `O carimbo ${i} é obrigatório pois existe uma assinatura cadastrada \n.`,
          );
        }
      }
    }

    if (errors.length > 0) {
      showAlert("error", "Campos Obrigatórios", errors.join("\n"));
      return;
    }

    if (isUpdate && !hasChanges) {
      handleClose();
      return;
    }

    setIsProcessing(true);
    const fd = new FormData();

    try {
      for (let i = 1; i <= 3; i++) {
        const isDrawMode = modes[i] === "draw";
        const pad = pads.current[i];
        const fileSig = files[`s${i}`];
        const fileStamp = files[`c${i}`];

        if (isDrawMode) {
          if (pad && !pad.isEmpty()) {
            const dataUrl = pad.toDataURL("image/png");
            const blob = await (await fetch(dataUrl)).blob();
            fd.append(
              `imagem_assinatura_${i}`,
              new File([blob], `signature_${i}.png`, { type: "image/png" }),
            );
          }
        } else if (fileSig) {
          fd.append(`imagem_assinatura_${i}`, fileSig);
        }

        if (fileStamp) {
          fd.append(`imagem_carimbo_${i}`, fileStamp);
        }
      }

      const res = await handleSave(fd);
      if (res?.result) {
        handleClose();
      } else {
        let errorMsg = res?.message || "Erro ao salvar.";
        if (res?.errors) {
          const detailedErrors = Object.values(res.errors).flat().join("\n");
          errorMsg = `${errorMsg}\n${detailedErrors}`;
        }
        showAlert("error", "Erro de Validação", errorMsg);
      }
    } catch (e) {
      console.error(e);
      showAlert("error", "Erro", "Erro ao processar os arquivos e salvar.");
    } finally {
      setIsProcessing(false);
    }
  };

  const renderTab = (i) => {
    const exSig = existingSignature?.[`caminho_arquivo_assinatura_${i}`];
    const exStmp = existingSignature?.[`caminho_arquivo_carimbo_${i}`];

    return (
      <div className="p-3 d-flex flex-column gap-2">
        <h6 className="text-center">Assinatura {i}</h6>
        <div className="text-center d-flex align-items-center justify-content-center gap-3">
          <label>Modo:</label>
          {["draw", "upload"].map((m) => (
            <label
              key={m}
              className="cursor-pointer d-flex align-items-center gap-1"
            >
              <input
                type="radio"
                checked={modes[i] === m}
                onChange={() => setModes((prev) => ({ ...prev, [i]: m }))}
              />{" "}
              {m === "draw" ? "Desenhar" : "Upload"}
            </label>
          ))}
        </div>

        {modes[i] === "draw" ? (
          <div className="text-center d-flex flex-column align-items-center gap-2">
            <div
              className="mx-auto"
              style={{
                border: "1px solid #ccc",
                width: "100%",
                maxWidth: "600px",
                height: "250px",
                backgroundColor: "#fff",
              }}
            >
              <canvas
                ref={(el) => (canvasRefs.current[i] = el)}
                style={{ width: "100%", height: "100%", display: "block" }}
              />
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => pads.current[i]?.clear()}
            >
              Limpar
            </Button>
            <div className="text-muted" style={{ fontSize: "0.75rem" }}>
              Dica: A resolução ideal para assinaturas é de aprox. 600x200px.
              <br />
              Dica: Use o site{" "}
              <a
                href="https://www.remove.bg"
                target="_blank"
                rel="noopener noreferrer"
              >
                remove.bg
              </a>{" "}
              para remover o fundo de suas imagens.
            </div>
          </div>
        ) : (
          <div className="text-center d-flex flex-column align-items-center gap-1">
            <input
              type="file"
              className="form-control"
              accept=".png, .jpg, .jpeg"
              onChange={(e) => handleFile(e, i, "sig")}
            />
            <div className="pb-2 text-muted" style={{ fontSize: "0.75rem" }}>
              Dica: A resolução ideal para assinaturas é de aprox. 600x200px.{" "}
              <br />
              Dica: Use o site{" "}
              <a
                href="https://www.remove.bg"
                target="_blank"
                rel="noopener noreferrer"
              >
                remove.bg
              </a>{" "}
              para remover o fundo de suas imagens.
              <br />
              <strong className="text-dark">
                Recomendamos o uso de arquivos PNG com fundo transparente.
              </strong>
            </div>
            {previews[`s${i}`] && (
              <div className="text-center pt-2">
                <img
                  src={previews[`s${i}`]}
                  alt="Preview"
                  className="img-fluid border rounded p-1"
                  style={{ maxHeight: 200, maxWidth: "100%" }}
                />
              </div>
            )}
          </div>
        )}
        {exSig && (
          <div className="pb-4 text-center">
            <small className="d-block text-muted pb-1">Atual:</small>
            <img
              src={STORAGE_URL + exSig}
              style={{ maxHeight: 100, maxWidth: "100%" }}
              alt="Atual"
              className="border rounded p-1"
            />
          </div>
        )}

        <hr />
        <h6 className="text-center">Carimbo {i}</h6>
        <div className="text-center d-flex flex-column align-items-center gap-1">
          <input
            type="file"
            className="form-control"
            accept=".png, .jpg, .jpeg"
            onChange={(e) => handleFile(e, i, "stamp")}
          />
          <div className="pb-2 text-muted" style={{ fontSize: "0.75rem" }}>
            Dica: A resolução ideal para carimbos é de aprox. 600x200px. <br />
            Dica: Use o site{" "}
            <a
              href="https://www.remove.bg"
              target="_blank"
              rel="noopener noreferrer"
            >
              remove.bg
            </a>{" "}
            para remover o fundo de suas imagens.
            <br />
            <strong className="text-dark">
              Recomendamos o uso de arquivos PNG com fundo transparente.
            </strong>
          </div>
          {previews[`c${i}`] && (
            <div className="text-center pt-2">
              <img
                src={previews[`c${i}`]}
                alt="Preview"
                className="img-fluid border rounded p-1"
                style={{ maxHeight: 200, maxWidth: "100%" }}
              />
            </div>
          )}
        </div>
        {exStmp && (
          <div className="text-center">
            <small className="d-block text-muted pb-1">Atual:</small>
            <img
              src={STORAGE_URL + exStmp}
              style={{ maxHeight: 100, maxWidth: "100%" }}
              alt="Atual"
              className="border rounded p-1"
            />
          </div>
        )}
      </div>
    );
  };

  const renderTabNumber = ()=>{
    console.log(user);
    if(user.email=='gislaina.sleal@adm.educacao.pe.gov.br'){
      return [1];
    }
    else if(user.email=='izabel.cprocha@adm.educacao.pe.gov.br'){
      return [2];
    }
    else if(user.email=='ana.elizabete1962@adm.educacao.pe.gov.br'){
      return [3];
    }

    return [1, 2, 3];
  }

  return (
    <>
      {alert}
      <Modal
        show={show}
        onHide={handleClose}
        onEntered={() => setModalEntered(true)}
        size="lg"
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Gerenciar Assinaturas</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ position: "relative" }}>
          {isProcessing && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={50} />
              <h5 className="mt-3">Salvando...</h5>
            </div>
          )}
          {loading || imagesLoading ? (
            <div className="text-center py-5">
              <CircularProgress />
            </div>
          ) : (
            <Tabs
              activeKey={key}
              onSelect={setKey}
              className="pb-3"
              mountOnEnter
              unmountOnExit={false}
            >
              {renderTabNumber().map((i) => (
                <Tab key={i} eventKey={`${i}`} title={`Assinatura ${i}`}>
                  {renderTab(i)}
                </Tab>
              ))}
            </Tabs>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isProcessing}
          >
            Cancelar
          </Button>
          <Button variant="primary" onClick={save} disabled={isProcessing}>
            {isProcessing ? (
              <span className="d-flex align-items-center gap-2">
                <CircularProgress size={16} color="inherit" />
                Salvando...
              </span>
            ) : (
              "Salvar"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default SignatureFormModal;
